import React, { useEffect, useState } from "react";
import Nav from "./Nav";
import SideMenu from "./SideMenu";
import { Link, useLocation, useNavigate } from "react-router-dom";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000"
).replace(/\/+$/, "");

const SOCIAL_LINKS_ENDPOINT = `${API_BASE_URL}/api/social-links/social-links`;

const normalizeSocialText = (value = "") => {
  return String(value)
    .toLocaleLowerCase("tr-TR")
    .replace(/ı/g, "i")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
};

const normalizeSocialUrl = (value = "") => {
  let cleanUrl = String(value).trim();

  if (!cleanUrl) {
    return "";
  }

  if (!/^https?:\/\//i.test(cleanUrl)) {
    cleanUrl = `https://${cleanUrl.replace(/^\/+/, "")}`;
  }

  try {
    const parsedUrl = new URL(cleanUrl);

    if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
      return "";
    }

    return parsedUrl.href;
  } catch {
    return "";
  }
};

const SOCIAL_ICON_RULES = [
  {
    keys: ["linkedin", "linked in"],
    icon: "fab fa-linkedin-in",
  },
  {
    keys: ["instagram", "insta"],
    icon: "fab fa-instagram",
  },
  {
    keys: ["youtube", "youtu be"],
    icon: "fab fa-youtube",
  },
  {
    keys: ["facebook", "fb"],
    icon: "fab fa-facebook-f",
  },
  {
    keys: ["twitter", "x com"],
    icon: "fab fa-twitter",
  },
  {
    keys: ["tiktok", "tik tok"],
    icon: "fab fa-tiktok",
  },
  {
    keys: ["whatsapp", "wa me"],
    icon: "fab fa-whatsapp",
  },
  {
    keys: ["telegram", "t me"],
    icon: "fab fa-telegram-plane",
  },
  {
    keys: ["pinterest"],
    icon: "fab fa-pinterest-p",
  },
  {
    keys: ["snapchat"],
    icon: "fab fa-snapchat-ghost",
  },
  {
    keys: ["github"],
    icon: "fab fa-github",
  },
  {
    keys: ["gitlab"],
    icon: "fab fa-gitlab",
  },
  {
    keys: ["behance"],
    icon: "fab fa-behance",
  },
  {
    keys: ["dribbble"],
    icon: "fab fa-dribbble",
  },
  {
    keys: ["discord"],
    icon: "fab fa-discord",
  },
  {
    keys: ["medium"],
    icon: "fab fa-medium-m",
  },
  {
    keys: ["reddit"],
    icon: "fab fa-reddit-alien",
  },
  {
    keys: ["tumblr"],
    icon: "fab fa-tumblr",
  },
  {
    keys: ["vimeo"],
    icon: "fab fa-vimeo-v",
  },
  {
    keys: ["flickr"],
    icon: "fab fa-flickr",
  },
  {
    keys: ["spotify"],
    icon: "fab fa-spotify",
  },
  {
    keys: ["soundcloud", "sound cloud"],
    icon: "fab fa-soundcloud",
  },
  {
    keys: ["twitch"],
    icon: "fab fa-twitch",
  },
  {
    keys: ["skype"],
    icon: "fab fa-skype",
  },
  {
    keys: ["slack"],
    icon: "fab fa-slack",
  },
  {
    keys: ["stack overflow", "stackoverflow"],
    icon: "fab fa-stack-overflow",
  },
  {
    keys: ["wechat", "weixin"],
    icon: "fab fa-weixin",
  },
  {
    keys: ["line me"],
    icon: "fab fa-line",
  },
  {
    keys: ["xing"],
    icon: "fab fa-xing",
  },
  {
    keys: ["vk", "vkontakte"],
    icon: "fab fa-vk",
  },
  {
    keys: ["etsy"],
    icon: "fab fa-etsy",
  },
  {
    keys: ["threads"],
    icon: "fas fa-at",
  },
];

const getSocialIcon = (title, link) => {
  const searchValue = normalizeSocialText(`${title || ""} ${link || ""}`);

  const matchedRule = SOCIAL_ICON_RULES.find((rule) =>
    rule.keys.some((key) => searchValue.includes(normalizeSocialText(key))),
  );

  return matchedRule?.icon || "fas fa-globe";
};

const resolveHeaderLogoUrl = (value) => {
  const cleanValue = String(value || "").trim();

  if (!cleanValue) {
    return "";
  }

  if (cleanValue.startsWith("http://") || cleanValue.startsWith("https://")) {
    return cleanValue;
  }

  if (cleanValue.startsWith("/cdn/")) {
    return `${API_BASE_URL}${cleanValue}`;
  }

  if (cleanValue.startsWith("cdn/")) {
    return `${API_BASE_URL}/${cleanValue}`;
  }

  if (cleanValue.startsWith("/")) {
    return `${API_BASE_URL}${cleanValue}`;
  }

  return `${API_BASE_URL}/cdn/${cleanValue}`;
};

const getFirstEmail = (value) => {
  const emails =
    String(value || "").match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi) || [];

  return emails[0] || "";
};

const getFirstPhone = (value) => {
  return (
    String(value || "")
      .split(/[\n,;|]+/)
      .map((item) => item.trim())
      .find(Boolean) || ""
  );
};

const createPhoneHref = (phone) => {
  const cleanPhone = String(phone || "").replace(/[^\d+]/g, "");

  return cleanPhone ? `tel:${cleanPhone}` : undefined;
};

const normalizeSearchText = (value = "") => {
  return String(value)
    .toLocaleLowerCase("tr-TR")
    .replace(/ı/g, "i")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/[^a-z0-9\s]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const getSearchResultScore = (result, query) => {
  const normalizedQuery = normalizeSearchText(query);

  const title = normalizeSearchText(result?.title);
  const snippet = normalizeSearchText(result?.snippet);

  const hierarchy = Array.isArray(result?.hierarchy)
    ? result.hierarchy.map((item) => normalizeSearchText(item))
    : [];

  let score = 0;

  /*
   * 1. Başlık tamamen aynıysa en üstte.
   */
  if (title === normalizedQuery) {
    score += 1000;
  }

  /*
   * 2. Başlık aranan kelimeyle başlıyorsa.
   *
   * Örnek:
   * "so" -> "Soğuk Çekme..."
   */
  if (title.startsWith(normalizedQuery)) {
    score += 800;
  }

  /*
   * 3. Başlığın herhangi bir kelimesi aramayla başlıyorsa.
   */
  const titleWords = title.split(" ");

  if (titleWords.some((word) => word.startsWith(normalizedQuery))) {
    score += 650;
  }

  /*
   * 4. Başlığın herhangi bir yerinde geçiyorsa.
   */
  if (title.includes(normalizedQuery)) {
    score += 500;
  }

  /*
   * 5. Kategori / hiyerarşi eşleşmeleri.
   */
  hierarchy.forEach((item) => {
    if (item === normalizedQuery) {
      score += 400;
    } else if (item.startsWith(normalizedQuery)) {
      score += 300;
    } else if (item.includes(normalizedQuery)) {
      score += 200;
    }
  });

  /*
   * 6. Açıklamada geçiyorsa daha düşük öncelik.
   */
  if (snippet.includes(normalizedQuery)) {
    score += 100;
  }

  return score;
};

const sortSearchResults = (results, query) => {
  return [...results]
    .map((result, index) => ({
      result,
      index,
      score: getSearchResultScore(result, query),
    }))
    .sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }

      /*
       * Aynı skordaysa API'den gelen mevcut sırayı bozma.
       */
      return a.index - b.index;
    })
    .map((item) => item.result);
};

const escapeSearchRegExp = (value = "") => {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

const HeaderHighlightText = ({ text, query }) => {
  const cleanText = String(text || "");
  const cleanQuery = String(query || "").trim();

  if (!cleanText || !cleanQuery) {
    return cleanText;
  }

  const regex = new RegExp(`(${escapeSearchRegExp(cleanQuery)})`, "gi");

  const parts = cleanText.split(regex);

  return (
    <>
      {parts.map((part, index) => {
        const isMatch =
          part.toLocaleLowerCase("tr-TR") ===
          cleanQuery.toLocaleLowerCase("tr-TR");

        if (isMatch) {
          return (
            <mark key={index} className="demirsan-live-search-highlight">
              {part}
            </mark>
          );
        }

        return <React.Fragment key={index}>{part}</React.Fragment>;
      })}
    </>
  );
};

function HeaderOne() {
  const location = useLocation();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [isSticky, setIsSticky] = useState(false);

  const [isSearchVisible, setSearchVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);

  const [searchSuggestionsLoading, setSearchSuggestionsLoading] =
    useState(false);

  const navigate = useNavigate();

  const [headerLogo, setHeaderLogo] = useState("");

  const [officeContact, setOfficeContact] = useState({
    title: "",
    email: "",
    phone: "",
  });
  const [socialLinks, setSocialLinks] = useState([]);

  const currentLang = new URLSearchParams(location.search).get("lang") || "tr";
  const [customerServicePhone, setCustomerServicePhone] = useState("");
  const [headerEmail, setHeaderEmail] = useState("");
  const toggleSidebar = () => {
    setIsSidebarOpen((current) => !current);
  };

  useEffect(() => {
    const controller = new AbortController();

    const fetchHeaderData = async () => {
      try {
        const [generalResponse, contactResponse, settingsResponse] =
          await Promise.all([
            fetch(
              `${API_BASE_URL}/api/general?lang=${encodeURIComponent(currentLang)}`,
              {
                method: "GET",
                headers: {
                  Accept: "application/json",
                },
                signal: controller.signal,
              },
            ),

            fetch(
              `${API_BASE_URL}/api/contact/contact-info?lang=${encodeURIComponent(
                currentLang,
              )}`,
              {
                method: "GET",
                headers: {
                  Accept: "application/json",
                },
                signal: controller.signal,
              },
            ),

            fetch(`${API_BASE_URL}/api/settings`, {
              method: "GET",
              headers: {
                Accept: "application/json",
              },
              signal: controller.signal,
            }),
          ]);

        const generalData = await generalResponse.json().catch(() => ({}));
        const contactData = await contactResponse.json().catch(() => []);
        const settingsData = await settingsResponse.json().catch(() => ({}));

        if (!settingsResponse.ok) {
          throw new Error(
            settingsData?.message || "Müşteri hizmetleri numarası alınamadı.",
          );
        }

        if (!generalResponse.ok) {
          throw new Error(generalData.message || "Genel ayarlar alınamadı.");
        }

        if (!contactResponse.ok) {
          throw new Error(
            contactData.message || "İletişim bilgileri alınamadı.",
          );
        }

        const settingsRecord = Array.isArray(settingsData)
          ? settingsData[0]
          : settingsData;

        setCustomerServicePhone(
          String(settingsRecord?.customer_service_phone || "").trim(),
        );

        setHeaderEmail(String(settingsRecord?.receiver_email || "").trim());

        setHeaderLogo(resolveHeaderLogoUrl(generalData.logo_header));

        const normalizedContacts = Array.isArray(contactData)
          ? contactData
          : contactData
            ? [contactData]
            : [];

        /*
         * title veya unvan alanında
         * "Ofis" geçen iletişim kaydını bulur.
         *
         * Örnek:
         * Ofis
         * İstanbul Ofis
         * Merkez Ofis
         */
        const officeRecord = normalizedContacts.find((contact) => {
          const contactTitle = `${contact.title || ""} ${
            contact.unvan || ""
          }`.toLocaleLowerCase("tr-TR");

          return contactTitle.includes("ofis");
        });

        if (officeRecord) {
          setOfficeContact({
            title: officeRecord.title || officeRecord.unvan || "Ofis",

            email: getFirstEmail(officeRecord.email),

            phone: getFirstPhone(officeRecord.phone),
          });
        } else {
          setOfficeContact({
            title: "",
            email: "",
            phone: "",
          });
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("HEADER_DATA_ERROR:", error);
        }
      }
    };

    fetchHeaderData();

    return () => {
      controller.abort();
    };
  }, [currentLang]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchSocialLinks = async () => {
      try {
        const response = await fetch(
          `${SOCIAL_LINKS_ENDPOINT}?lang=${encodeURIComponent(currentLang)}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          },
        );

        const responseData = await response.json().catch(() => []);

        if (!response.ok) {
          throw new Error(
            responseData?.message || "Sosyal medya bağlantıları alınamadı.",
          );
        }

        const normalizedLinks = (
          Array.isArray(responseData) ? responseData : []
        )
          .map((item) => {
            const title = String(
              item?.title || item?.["tıtle"] || "Sosyal Medya",
            ).trim();

            const link = normalizeSocialUrl(item?.link);

            return {
              title,
              link,
            };
          })
          .filter((item) => item.link);

        setSocialLinks(normalizedLinks);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("SOCIAL_LINKS_ERROR:", error);

          setSocialLinks([]);
        }
      }
    };

    fetchSocialLinks();

    return () => {
      controller.abort();
    };
  }, [currentLang]);

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 150);
    };

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /*
|--------------------------------------------------------------------------
| CANLI ARAMA ÖNERİLERİ
|--------------------------------------------------------------------------
*/

  useEffect(() => {
    const query = searchText.trim();

    /*
     * Arama popup'ı kapalıysa veya
     * 2 karakterden azsa API'ye gitme.
     */
    if (!isSearchVisible || query.length < 2) {
      setSearchSuggestions([]);
      setSearchSuggestionsLoading(false);

      return;
    }

    const controller = new AbortController();

    /*
     * Her tuşta API'ye abanmasın.
     * Kullanıcı yazmayı bıraktıktan 250ms sonra ara.
     */
    const timeoutId = setTimeout(async () => {
      try {
        setSearchSuggestionsLoading(true);

        const response = await fetch(
          `${API_BASE_URL}/api/search?q=${encodeURIComponent(
            query,
          )}&lang=${encodeURIComponent(currentLang)}`,
          {
            method: "GET",

            headers: {
              Accept: "application/json",
            },

            signal: controller.signal,
          },
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data?.message || "Arama yapılamadı.");
        }

        const results = Array.isArray(data?.results) ? data.results : [];

        /*
         * En alakalıları üstte göster.
         * Header'da ilk 7 sonuç yeterli.
         */
        const sortedResults = sortSearchResults(results, query).slice(0, 7);

        setSearchSuggestions(sortedResults);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("HEADER_LIVE_SEARCH_ERROR:", error);

          setSearchSuggestions([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setSearchSuggestionsLoading(false);
        }
      }
    }, 250);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [searchText, currentLang, isSearchVisible]);
  /*
|--------------------------------------------------------------------------
| ARAMA
|--------------------------------------------------------------------------
*/

  const handleSearchClick = () => {
    setSearchVisible(true);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();

    const query = searchText.trim();

    if (!query) {
      return;
    }

    setSearchVisible(false);

    navigate(
      `/search?q=${encodeURIComponent(query)}&lang=${encodeURIComponent(
        currentLang,
      )}`,
    );
  };

  const handleSuggestionClick = (result) => {
    if (!result?.url) {
      return;
    }

    let targetUrl = result.url;

    /*
     * Türkçe dışındaki dil seçiliyse
     * ilgili sayfada dili koru.
     */
    if (currentLang && currentLang !== "tr" && !targetUrl.includes("lang=")) {
      const separator = targetUrl.includes("?") ? "&" : "?";

      targetUrl = `${targetUrl}${separator}lang=${encodeURIComponent(
        currentLang,
      )}`;
    }

    setSearchVisible(false);
    setSearchSuggestions([]);
    setSearchText("");

    navigate(targetUrl);
  };

  const handleCloseClick = () => {
    setSearchVisible(false);
    setSearchSuggestions([]);
    setSearchText("");
  };

  const handleBackgroundClick = () => {
    setSearchVisible(false);
    setSearchSuggestions([]);
    setSearchText("");
  };

  return (
    <div
      className={`demirsan-header-wrapper ${isSticky ? "sticky-active" : ""}`}
    >
      <style>
        {`
           .demirsan-header {
            position: relative;
            width: 100%;
            z-index: 9999;
          }
 
          .demirsan-header .header-main-one {
            position: relative;
            padding: 0 !important;
            overflow: visible !important;
          }
 
          .demirsan-header .header-main-one .container,
          .demirsan-header .header-main-one .row,
          .demirsan-header .main-header,
          .demirsan-header .mainmenu-nav,
          .demirsan-header .mainmenu {
            overflow: visible !important;
          }
 
          .demirsan-header .header-main-one .row {
            align-items: center;
            min-height: 100px;
          }
 
          .demirsan-header .thumbnail {
            display: flex;
            align-items: center;
            height: 82px;
          }
 
          .demirsan-header .main-header {
            display: flex;
            align-items: center;
            justify-content: center;
 
            width: 100%;
            height: 100px !important;
            min-height: 100px !important;
 
            padding: 0 !important;
          }
 
          .demirsan-header .main-header > div:first-child {
            width: 100%;
          }
 
          .demirsan-header .mainmenu-nav {
            width: 100%;
          }
 
          .demirsan-header .mainmenu {
            display: flex !important;
            align-items: center;
            justify-content: center !important;
 
            gap: 28px;
 
            width: 100%;
            margin: 0 !important;
            padding: 0 !important;
          }
 
          .demirsan-header .mainmenu > li {
            margin: 0 !important;
            padding: 0 !important;
          }
 
          .demirsan-header .mainmenu > li > a {
            position: relative;
 
            display: block;
 
            padding: 8px 0 !important;
 
            font-size: 16px !important;
            font-weight: 600 !important;
            line-height: 1.2;
 
            white-space: nowrap;
          }
 
          .demirsan-header .mainmenu > li > a::after {
            top: auto !important;
            bottom: -5px !important;
          }
 
          /* MÜŞTERİ HİZMETLERİ */
          .demirsan-customer-service {
            display: flex;
            align-items: center;
            justify-content: flex-end;
 
            gap: 13px;
 
            width: 100%;
            height: 100px;
          }
 
          .demirsan-customer-service-icon {
            display: flex;
            align-items: center;
            justify-content: center;
 
            width: 50px;
            height: 50px;
            flex: 0 0 50px;
 
            color: #c4265a;
 
            font-size: 40px;
            line-height: 1;
          }
 
          .demirsan-customer-service-icon img {
            display: block;
 
            width: 50px;
            height: 50px;
 
            object-fit: contain;
          }
 
          .demirsan-customer-service-content {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
 
            min-width: 0;
          }
 
          .demirsan-customer-service-title {
            display: block;
 
            margin: 0 0 4px;
 
            color: #1a1a1a;
 
            font-size: 16px;
            font-weight: 700;
            line-height: 1.25;
 
            white-space: nowrap;
          }
 
          .demirsan-customer-service-phone {
            display: block;
 
            color: #c4265a !important;
 
            font-size: 16px;
            font-weight: 700;
            line-height: 1.25;
 
            text-decoration: none !important;
            white-space: nowrap;
 
            transition: color 0.2s ease;
          }
 
          .demirsan-customer-service-phone:hover {
            color: #97183e !important;
          }
 
          .demirsan-customer-service-phone::before,
          .demirsan-customer-service-phone::after {
            display: none !important;
            content: none !important;
          }
 
          /* HEADER TAMAMI STICKY */
          .demirsan-header.sticky {
            position: fixed !important;
            top: 0;
            right: 0;
            left: 0;
 
            width: 100%;
 
            z-index: 99999;
 
            animation: demirsanHeaderDown 0.25s ease;
 
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.14);
          }
 
          .demirsan-header.sticky .header-top,
          .demirsan-header.sticky .header-main-one {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            transform: none !important;
          }
 
          .demirsan-header-wrapper.sticky-active {
            padding-top: 145px;
          }
 
          @keyframes demirsanHeaderDown {
            from {
              transform: translateY(-100%);
            }
 
            to {
              transform: translateY(0);
            }
          }
 
          @media (min-width: 1200px) {
            .demirsan-header .button-area {
              display: none !important;
            }
          }
 
          @media (max-width: 1399.98px) {
            .demirsan-header .mainmenu {
              gap: 18px;
            }
 
            .demirsan-header .mainmenu > li > a {
              font-size: 14px !important;
            }
 
            .demirsan-customer-service {
              gap: 9px;
            }
 
            .demirsan-customer-service-icon,
            .demirsan-customer-service-icon img {
              width: 42px;
              height: 42px;
            }
 
            .demirsan-customer-service-icon {
              flex-basis: 42px;
              font-size: 34px;
            }
              
 
            .demirsan-customer-service-title,
            .demirsan-customer-service-phone {
              font-size: 13px;
            }
          }
 
          @media (max-width: 1199.98px) {
            .demirsan-header .header-main-one .row,
            .demirsan-header .thumbnail,
            .demirsan-header .main-header {
              height: 72px !important;
              min-height: 72px !important;
            }
 
            .demirsan-header .main-header {
              justify-content: flex-end;
            }
 
            .demirsan-header .button-area {
              margin-left: auto;
            }
 
            .demirsan-customer-service {
              display: none !important;
            }
 
            .demirsan-header-wrapper.sticky-active {
              padding-top: 72px;
            }
          }
            /* Mobil yan menü açıkken arkadaki header tamamen gizlensin */
@media (max-width: 1199.98px) {
  .demirsan-header.sidebar-is-open {
    opacity: 0 !important;
    visibility: hidden !important;
    pointer-events: none !important;
  }
}
  .demirsan-customer-service-icon {
  display: flex;
  align-items: center;
  justify-content: center;
 
  width: 50px;
  height: 50px;
  flex: 0 0 50px;
 
  /* Üst menü kırmızısının daha yumuşak hali */
  color: #e3262e;
 
  background: transparent;
  border: none;
  box-shadow: none;
 
  font-size: 40px;
  font-weight: 300;
  line-height: 1;
 
  transition:
    color 0.2s ease,
    transform 0.2s ease;
}
 
.demirsan-customer-service-icon i {
  font-weight: 300 !important;
}
 
.demirsan-customer-service:hover
  .demirsan-customer-service-icon {
  color: #ed0b0b;
  transform: translateY(-1px);
}
 
.demirsan-customer-service-phone {
  display: block;
 
  color: #e3262e !important;
 
  font-size: 16px;
  font-weight: 600;
  line-height: 1.25;
 
  -webkit-text-stroke: 0.3px currentColor;
 
  text-decoration: none !important;
  white-space: nowrap;
 
  transition: color 0.2s ease;
}
 
.demirsan-customer-service-phone:hover {
  color: #ed0b0b !important;
}
  /* HEADER SATIRININ İKİNCİ SATIRA DÜŞMESİNİ ENGELLER */
.demirsan-header .demirsan-header-row {
  display: flex !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
 
  width: 100%;
  height: 100px !important;
  min-height: 100px !important;
 
  margin-right: calc(-0.5 * var(--bs-gutter-x));
  margin-left: calc(-0.5 * var(--bs-gutter-x));
}
 
/* BÜTÜN HEADER SÜTUNLARI AYNI YÜKSEKLİKTE */
.demirsan-header .demirsan-header-logo-column,
.demirsan-header .demirsan-header-menu-column,
.demirsan-header .demirsan-header-service-column {
  height: 100px !important;
  min-height: 100px !important;
}
 
/* LOGO */
.demirsan-header .demirsan-header-logo-column {
  display: flex !important;
  align-items: center !important;
}
 
.demirsan-header .thumbnail {
  display: flex !important;
  align-items: center !important;
 
  width: 100%;
  height: 100px !important;
 
  margin: 0 !important;
  padding: 0 !important;
}
 
.demirsan-header .demirsan-header-logo-image {
  display: block;

  width: auto;
  max-width: 260px;
  height: 62px;

  object-fit: contain;
}
 
/* MENÜ */
.demirsan-header .demirsan-header-menu-column {
  display: flex !important;
  align-items: center !important;
}
 
.demirsan-header .main-header {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
 
  width: 100%;
  height: 100px !important;
  min-height: 100px !important;
}
 
/* MÜŞTERİ HİZMETLERİ */
.demirsan-header .demirsan-header-service-column {
  align-items: center !important;
  justify-content: flex-end !important;
}
 
.demirsan-header .demirsan-customer-service {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-end !important;
 
  width: auto !important;
  height: auto !important;
  min-height: 0 !important;
 
  margin: 0 !important;
  padding: 0 !important;
}
 
/* MOBİL VE TABLET */
@media (max-width: 1199.98px) {
  .demirsan-header .demirsan-header-row {
    height: 72px !important;
    min-height: 72px !important;
  }
 
  .demirsan-header .demirsan-header-logo-column,
  .demirsan-header .demirsan-header-menu-column {
    height: 72px !important;
    min-height: 72px !important;
  }
 
  .demirsan-header .thumbnail,
  .demirsan-header .main-header {
    height: 72px !important;
    min-height: 72px !important;
  }
 
  .demirsan-header .demirsan-header-logo-image {
    max-width: 190px;
    height: 48px;
  }
}
  /* Sosyal medya ikonlarını birbirinden ayır */
.demirsan-header .social-wrapper-one {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-end !important;
  gap: 12px !important;
 
  margin: 0 !important;
  padding: 0 !important;
 
  list-style: none !important;
}
 
.demirsan-header .social-wrapper-one li {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
 
  width: auto !important;
  height: auto !important;
 
  margin: 0 !important;
  padding: 0 !important;
}
 
.demirsan-header .social-wrapper-one li a {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
 
  width: 26px !important;
  height: 26px !important;
 
  margin: 0 !important;
  padding: 0 !important;
 
  color: #ffffff !important;
  font-size: 14px !important;
  line-height: 1 !important;
 
  text-decoration: none !important;
}
 
.demirsan-header .social-wrapper-one li a i {
  display: block !important;
 
  margin: 0 !important;
  padding: 0 !important;
 
  color: #ffffff !important;
  font-size: 14px !important;
  line-height: 1 !important;
}
 
.demirsan-header .social-wrapper-one li a:hover {
  color: rgba(255, 255, 255, 0.75) !important;
  transform: translateY(-1px);
}
  /* Üst sağ alanı aynı hizada tut */
.demirsan-header .header-top .right {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-end !important;
 
  height: 100%;
  margin: 0 !important;
  padding: 0 !important;
}
 
/* İletişim menüsü */
.demirsan-header .header-top .top-nav {
  display: flex !important;
  align-items: center !important;
 
  margin: 0 !important;
  padding: 0 !important;
 
  list-style: none !important;
}
 
.demirsan-header .header-top .top-nav li,
.demirsan-header .header-top .top-nav li a {
  margin: 0 !important;
  padding: 0 !important;

  line-height: 26px !important;
}

.demirsan-header .header-top .top-nav li a::after {
  bottom: 0 !important;
}
 
/* Eski tema çizgilerini kaldır */
.demirsan-header .social-wrapper-one::before,
.demirsan-header .social-wrapper-one::after,
.demirsan-header .top-nav::before,
.demirsan-header .top-nav::after {
  display: none !important;
  content: none !important;
}
 
/* Sosyal medya alanı ve yeni çizgi */
.demirsan-header .social-wrapper-one {
  position: relative !important;
 
  display: flex !important;
  align-items: center !important;
  justify-content: flex-end !important;
 
  gap: 12px !important;
 
  margin: 0 0 0 18px !important;
  padding: 0 0 0 19px !important;
 
  list-style: none !important;
}
 
.demirsan-header .social-wrapper-one::before {
  content: "" !important;
 
  position: absolute !important;
  display: block !important;
 
  left: 0 !important;
  top: 50% !important;
 
  width: 1px !important;
  height: 20px !important;
 
  background: rgba(255, 255, 255, 0.5) !important;
 
  transform: translateY(-50%) !important;
}
  /*
|--------------------------------------------------------------------------
| HEADER SAĞ ARAÇLAR
|--------------------------------------------------------------------------
*/
 
.demirsan-header .demirsan-header-right-tools {
  display: flex;
  align-items: center;
  justify-content: flex-end;
 
  gap: 14px;
 
  width: 100%;
}
 
 
/*
|--------------------------------------------------------------------------
| MASAÜSTÜ ARAMA BUTONU
|--------------------------------------------------------------------------
*/
 
.demirsan-header .demirsan-header-search-button {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
 
  flex: 0 0 44px;
 
  width: 44px !important;
  min-width: 44px !important;
  max-width: 44px !important;
 
  height: 44px !important;
  min-height: 44px !important;
  max-height: 44px !important;
 
  margin: 0 !important;
  padding: 0 !important;
 
  border: 1px solid #e5e7eb !important;
  border-radius: 50% !important;
 
  background: #ffffff !important;
 
  color: #e3262e !important;
 
  font-size: 17px !important;
  line-height: 1 !important;
 
  cursor: pointer;
 
  transition:
    background-color .2s ease,
    color .2s ease,
    border-color .2s ease,
    transform .2s ease;
}
 
.demirsan-header .demirsan-header-search-button:hover {
  border-color: #e3262e !important;
 
  background: #e3262e !important;
 
  color: #ffffff !important;
 
  transform: translateY(-1px);
}
 
.demirsan-header .demirsan-header-search-button i {
  margin: 0 !important;
  padding: 0 !important;
 
  color: inherit !important;
 
  font-size: 17px !important;
  line-height: 1 !important;
}
 
 
/*
|--------------------------------------------------------------------------
| MOBİL ARAMA
|--------------------------------------------------------------------------
*/
 
.demirsan-mobile-search-button {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
 
  width: 45px !important;
  min-width: 45px !important;
  height: 45px !important;
 
  margin: 0 !important;
  padding: 0 !important;
}
 
.demirsan-mobile-search-button i {
  margin: 0 !important;
 
  font-size: 17px !important;
}
 
 
/*
|--------------------------------------------------------------------------
| SEARCH OVERLAY Z-INDEX
|--------------------------------------------------------------------------
*/
 
.demirsan-header-wrapper .search-input-area {
  z-index: 100001 !important;
}
 
.demirsan-header-wrapper #anywhere-home.bgshow {
  z-index: 100000 !important;
}
  @media (max-width: 1399.98px) {
 
  .demirsan-header .demirsan-header-right-tools {
    gap: 8px;
  }
 
  .demirsan-header .demirsan-header-search-button {
    flex-basis: 38px;
 
    width: 38px !important;
    min-width: 38px !important;
    max-width: 38px !important;
 
    height: 38px !important;
    min-height: 38px !important;
    max-height: 38px !important;
  }
 
  .demirsan-header .demirsan-header-search-button i {
    font-size: 15px !important;
  }
}
 
/*
|==========================================================================
| YENİ DÜZENLEME (bozmadan, üstüne ekleme):
| 1) Logo sola yaslı
| 2) Menü sayfanın tam ortasında
| 3) Arama butonu belirgin (dolu kırmızı, gölgeli)
|==========================================================================
*/
 
@media (min-width: 1200px) {
  /* Satırı 3 esnek bloğa böl: logo (sabit genişlik) - menü (kalan alan, ortalı) - araçlar (sabit genişlik) */
  .demirsan-header .demirsan-header-row {
    display: flex !important;
    justify-content: space-between !important;
  }
 
  .demirsan-header .demirsan-header-logo-column {
    flex: 0 0 auto !important;
    width: auto !important;
    max-width: none !important;
    padding-left: 0 !important;
  }
 
  .demirsan-header .demirsan-header-logo-column .thumbnail {
    padding-left: 0 !important;
    margin-left: 0 !important;
    justify-content: flex-start !important;
  }
 
  .demirsan-header .demirsan-header-menu-column {
    flex: 1 1 auto !important;
    width: auto !important;
    max-width: none !important;
 
    display: flex !important;
    justify-content: center !important;
  }
 
  .demirsan-header .demirsan-header-menu-column .main-header {
    justify-content: center !important;
  }
 
  .demirsan-header .demirsan-header-service-column {
    flex: 0 0 auto !important;
    width: auto !important;
    max-width: none !important;
    padding-right: 0 !important;
  }
}
 
/* ARAMA BUTONUNU DAHA BELİRGİN YAP: dolu kırmızı zemin + gölge */
.demirsan-header .demirsan-header-search-button {
  background: #e3262e !important;
  border-color: #e3262e !important;
  color: #ffffff !important;
 
  width: 48px !important;
  min-width: 48px !important;
  max-width: 48px !important;
 
  height: 48px !important;
  min-height: 48px !important;
  max-height: 48px !important;
 
  font-size: 18px !important;
 
  box-shadow: 0 4px 14px rgba(227, 38, 46, 0.35) !important;
}
 
.demirsan-header .demirsan-header-search-button i {
  font-size: 18px !important;
}
 
.demirsan-header .demirsan-header-search-button:hover {
  background: #97183e !important;
  border-color: #97183e !important;
 
  transform: translateY(-2px) scale(1.05);
 
  box-shadow: 0 6px 18px rgba(151, 24, 62, 0.4) !important;
}
 
@media (max-width: 1399.98px) {
  .demirsan-header .demirsan-header-search-button {
    width: 42px !important;
    min-width: 42px !important;
    max-width: 42px !important;
 
    height: 42px !important;
    min-height: 42px !important;
    max-height: 42px !important;
 
    font-size: 16px !important;
  }
 
  .demirsan-header .demirsan-header-search-button i {
    font-size: 16px !important;
  }
}
 
/* ARAMA KAPATMA (X) BUTONUNU SAĞ ÜSTE SABİTLE */
.demirsan-header-wrapper .search-input-area .search-close-icon {
  position: fixed !important;
 
  top: 28px !important;
  right: 28px !important;
  left: auto !important;
  bottom: auto !important;
  transform: none !important;
 
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
 
  width: 44px !important;
  height: 44px !important;
 
  border-radius: 50% !important;
  border: 1px solid rgba(255, 255, 255, 0.35) !important;
 
  color: #ffffff !important;
  font-size: 18px !important;
 
  z-index: 100002 !important;
 
  /* Panelle birlikte açılıp kapansın, ayrı ayrı zıplamasın */
  opacity: 0 !important;
  visibility: hidden !important;
  pointer-events: none !important;
 
  transition:
    opacity .25s ease,
    visibility .25s ease,
    background-color .2s ease,
    transform .2s ease;
}
 
.demirsan-header-wrapper .search-input-area.show .search-close-icon {
  opacity: 1 !important;
  visibility: visible !important;
  pointer-events: auto !important;
}
 
.demirsan-header-wrapper .search-input-area .search-close-icon:hover {
  background: rgba(255, 255, 255, 0.12) !important;
  transform: translateY(-1px) !important;
}
 
@media (max-width: 767.98px) {
  .demirsan-header-wrapper .search-input-area .search-close-icon {
    top: 18px !important;
    right: 18px !important;
 
    width: 38px !important;
    height: 38px !important;
 
    font-size: 16px !important;
  }
}
  /* MOBİL ARAMA + HAMBURGER AYNI TASARIM */
@media (max-width: 1199.98px) {
  .demirsan-header .button-area {
    display: flex !important;
    align-items: center !important;
    justify-content: flex-end !important;
    gap: 8px !important;
  }

  .demirsan-header .demirsan-mobile-action-button {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;

    width: 48px !important;
    min-width: 48px !important;
    max-width: 48px !important;

    height: 48px !important;
    min-height: 48px !important;
    max-height: 48px !important;

    margin: 0 !important;
    padding: 0 !important;

    background: #fdebed !important;
    border: none !important;
    border-radius: 16px !important;
    box-shadow: none !important;

    color: #172033 !important;
  }

  /* ARAMA İKONU */
  .demirsan-header .demirsan-mobile-action-button i {
    margin: 0 !important;
    padding: 0 !important;

    color: #172033 !important;
    font-size: 18px !important;
    line-height: 1 !important;
  }

  /* HAMBURGER İKONU */
  .demirsan-header .demirsan-mobile-action-button img {
    width: 20px !important;
    height: 20px !important;
    margin: 0 !important;
    padding: 0 !important;
    object-fit: contain !important;
  }
}
  /*
|--------------------------------------------------------------------------
| HEADER CANLI ARAMA
|--------------------------------------------------------------------------
*/

.demirsan-live-search-wrapper {
  position: relative !important;

  width: 100%;
}

.demirsan-live-search-results {
  position: absolute;

  top: calc(100% + 12px);
  left: 0;
  right: 0;

  width: 100%;
  max-height: 480px;

  overflow-y: auto;

  background: #ffffff;

  border: 1px solid #e5e7eb;
  border-radius: 12px;

  box-shadow:
    0 18px 45px
    rgba(0, 0, 0, 0.16);

  z-index: 100005;
}


/*
|--------------------------------------------------------------------------
| TEK SONUÇ
|--------------------------------------------------------------------------
*/

.demirsan-live-search-item {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;

  gap: 18px;

  width: 100% !important;

  margin: 0 !important;
  padding: 15px 18px !important;

  border: 0 !important;
  border-bottom: 1px solid #eef0f3 !important;

  border-radius: 0 !important;

  background: #ffffff !important;

  text-align: left !important;

  cursor: pointer;

  transition:
    background-color .2s ease,
    padding-left .2s ease;
}

.demirsan-live-search-item:first-child {
  border-radius: 12px 12px 0 0 !important;
}

.demirsan-live-search-item:hover {
  background: #fff5f5 !important;

  padding-left: 22px !important;
}


/*
|--------------------------------------------------------------------------
| SONUÇ İÇERİĞİ
|--------------------------------------------------------------------------
*/

.demirsan-live-search-item-content {
  flex: 1;

  min-width: 0;
}



.demirsan-live-search-title {
  overflow: hidden;

  color: #172033;

  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;

  text-overflow: ellipsis;

  white-space: nowrap;
}

.demirsan-live-search-snippet {
  display: -webkit-box;

  margin-top: 4px;

  overflow: hidden;

  color: #727b8b;

  font-size: 12px;
  line-height: 1.5;

  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
}


/*
|--------------------------------------------------------------------------
| OK
|--------------------------------------------------------------------------
*/

.demirsan-live-search-arrow {
  display: flex;
  align-items: center;
  justify-content: center;

  flex: 0 0 34px;

  width: 34px;
  height: 34px;

  border-radius: 50%;

  background: #fff0f1;

  color: #e3262e;

  font-size: 12px;

  transition:
    background-color .2s ease,
    color .2s ease,
    transform .2s ease;
}

.demirsan-live-search-item:hover
.demirsan-live-search-arrow {
  background: #e3262e;

  color: #ffffff;

  transform: translateX(2px);
}


/*
|--------------------------------------------------------------------------
| LOADING / BOŞ
|--------------------------------------------------------------------------
*/

.demirsan-live-search-status {
  display: flex;
  align-items: center;
  justify-content: center;

  gap: 9px;

  padding: 22px;

  color: #7a8495;

  font-size: 13px;
}


/*
|--------------------------------------------------------------------------
| TÜM SONUÇLARI GÖR
|--------------------------------------------------------------------------
*/

.demirsan-live-search-all {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;

  gap: 15px;

  width: 100% !important;

  margin: 0 !important;
  padding: 14px 18px !important;

  border: 0 !important;

  border-radius: 0 0 12px 12px !important;

  background: #f8f9fb !important;

  color: #e3262e !important;

  font-size: 13px !important;
  font-weight: 700 !important;

  text-align: left !important;

  cursor: pointer;

  transition: background-color .2s ease;
}

.demirsan-live-search-all:hover {
  background: #fff0f1 !important;
}


/*
|--------------------------------------------------------------------------
| MOBİL
|--------------------------------------------------------------------------
*/

@media (max-width: 767.98px) {
  .demirsan-live-search-results {
    top: calc(100% + 8px);

    max-height: 400px;
  }

  .demirsan-live-search-item {
    padding: 13px 14px !important;
  }

  .demirsan-live-search-item:hover {
    padding-left: 14px !important;
  }

  .demirsan-live-search-title {
    font-size: 14px;
  }

  .demirsan-live-search-snippet {
    font-size: 11px;
  }
}
.demirsan-live-search-highlight {
  display: inline;
  margin: 0;
  padding: 0;
  border-radius: 0;
  background: transparent;
  color: #d71920 !important;
  font: inherit;
  font-weight: 700;
}
  /*
|--------------------------------------------------------------------------
| HEADER CANLI ARAMA - HİYERARŞİ
|--------------------------------------------------------------------------
*/

.demirsan-live-search-hierarchy {
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  gap: 7px;

  margin-bottom: 8px;
}


/* OVAL GRİ ETİKET */

.demirsan-live-search-hierarchy-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;

  width: auto;

  padding: 4px 10px 4px 8px;

  border: 1px solid #e7e9ee;
  border-radius: 999px;

  background: #f6f7f9;
  color: #5b6577;

  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.4px;
  line-height: 1.5;

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;
}


/* ETİKETİN SOLUNDAKİ KÜÇÜK NOKTA */

.demirsan-live-search-hierarchy-item::before {
  content: "";

  flex: 0 0 5px;

  width: 5px;
  height: 5px;

  border-radius: 50%;

  background: #c7cdd8;

  transition: background-color 0.2s ease;
}


/* ARADAKİ > */

.demirsan-live-search-hierarchy-separator {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  min-width: 10px;

  color: #929aaa;

  font-size: 18px;
  font-weight: 800;
  line-height: 1;

  transform: translateY(-1px);

  transition: color 0.2s ease;
}


/*
|--------------------------------------------------------------------------
| SONUCUN ÜSTÜNE GELİNCE KIRMIZI
|--------------------------------------------------------------------------
*/

.demirsan-live-search-item:hover
.demirsan-live-search-hierarchy-item {
  border-color: rgba(227, 38, 46, 0.25);

  background: #fff0f1;

  color: #d92129;
}

.demirsan-live-search-item:hover
.demirsan-live-search-hierarchy-item::before {
  background: #e3262e;
}

.demirsan-live-search-item:hover
.demirsan-live-search-hierarchy-separator {
  color: #d92129;
}
        `}
      </style>

      <header
        className={`header--sticky header-one demirsan-header ${
          isSticky ? "sticky" : ""
        } ${isSidebarOpen ? "sidebar-is-open" : ""}`}
      >
        {/* ÜST KIRMIZI ALAN */}
        <div className="header-top header-top-one bg-1">
          <div className="container">
            <div className="row">
              <div className="col-lg-6 d-xl-block d-none">
                <div className="left">
                  {headerEmail && (
                    <div className="mail">
                      <a href={`mailto:${headerEmail}`}>
                        <i className="fal fa-envelope" />

                        {headerEmail}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <div className="col-lg-6 d-xl-block d-none">
                <div className="right">
                  <ul className="top-nav">
                    <li>
                      <Link to="/contactus">İletişim</Link>
                    </li>
                  </ul>

                  {socialLinks.length > 0 && (
                    <ul className="social-wrapper-one">
                      {socialLinks.map((social, index) => (
                        <li key={`${social.title}-${social.link}-${index}`}>
                          <a
                            href={social.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Demirsan ${social.title}`}
                            title={social.title}
                          >
                            <i
                              className={getSocialIcon(
                                social.title,
                                social.link,
                              )}
                            />
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ANA HEADER */}
        <div className="header-main-one bg-white">
          <div className="container">
            <div className="row flex-nowrap align-items-center demirsan-header-row">
              {/* LOGO */}
              <div className="col-xl-3 col-lg-4 col-md-4 col-sm-4 col-4 demirsan-header-logo-column">
                <div className="thumbnail">
                  <Link to={`/`}>
                    <img
                      src={headerLogo || "/assets/images/logo/logo-2.png"}
                      alt="Demirsan Logo"
                      className="demirsan-header-logo-image"
                      onError={(event) => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src =
                          "/assets/images/logo/logo-2.png";
                      }}
                    />
                  </Link>
                </div>
              </div>

              {/* ANA MENÜ */}
              <div className="col-xl-6 col-lg-8 col-md-8 col-sm-8 col-8 demirsan-header-menu-column">
                <div className="main-header">
                  <Nav />

                  <div className="button-area">
                    {/* MOBİL ARAMA */}
                    <button
                      type="button"
                      className="rts-btn btn-primary-alta demirsan-mobile-action-button"
                      onClick={handleSearchClick}
                      aria-label="Sitede ara"
                      title="Ara"
                    >
                      <i className="far fa-search" />
                    </button>

                    {/* MOBİL MENÜ */}
                    <button
                      id="menu-btn"
                      type="button"
                      className="menu rts-btn btn-primary-alta demirsan-mobile-action-button"
                      onClick={toggleSidebar}
                      aria-label="Menüyü aç"
                    >
                      <img
                        className="menu-dark"
                        src="/assets/images/icon/menu.png"
                        alt=""
                      />

                      <img
                        className="menu-light"
                        src="/assets/images/icon/menu-light.png"
                        alt=""
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* ARAMA + MÜŞTERİ HİZMETLERİ */}
              <div className="col-xl-3 d-none d-xl-flex align-items-center justify-content-end demirsan-header-service-column">
                <div className="demirsan-header-right-tools">
                  {/* ARAMA */}
                  <button
                    type="button"
                    className="rts-btn btn-primary-alta demirsan-header-tool-button"
                    onClick={handleSearchClick}
                    aria-label="Sitede ara"
                    title="Ara"
                  >
                    <i className="far fa-search" />
                  </button>

                  {/* MÜŞTERİ HİZMETLERİ */}
                  {customerServicePhone && (
                    <div className="demirsan-customer-service">
                      <div
                        className="demirsan-customer-service-icon"
                        aria-hidden="true"
                      >
                        <i className="fal fa-headset" />
                      </div>

                      <div className="demirsan-customer-service-content">
                        <span className="demirsan-customer-service-title">
                          Müşteri Hizmetleri
                        </span>

                        <a
                          className="demirsan-customer-service-phone"
                          href={createPhoneHref(customerServicePhone)}
                        >
                          {customerServicePhone}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className={`search-input-area ${isSearchVisible ? "show" : ""}`}>
        <div className="container">
          <div className="search-input-inner demirsan-live-search-wrapper">
            <div className="input-div">
              <input
                id="searchInput1"
                className="search-input"
                type="text"
                placeholder="Anahtar kelime ile ara"
                value={searchText}
                autoComplete="off"
                onChange={(event) => {
                  setSearchText(event.target.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    handleSearchSubmit(event);
                  }
                }}
              />

              <button
                type="button"
                aria-label="Ara"
                onClick={handleSearchSubmit}
              >
                <i className="far fa-search" />
              </button>
            </div>

            {/* ===========================================
      CANLI ARAMA SONUÇLARI
  ============================================ */}

            {searchText.trim().length >= 2 && (
              <div className="demirsan-live-search-results">
                {searchSuggestionsLoading ? (
                  <div className="demirsan-live-search-status">
                    <i className="far fa-spinner fa-spin" />

                    <span>Aranıyor...</span>
                  </div>
                ) : searchSuggestions.length > 0 ? (
                  <>
                    {searchSuggestions.map((result, index) => {
                      const hierarchy = Array.isArray(result.hierarchy)
                        ? result.hierarchy
                        : [];

                      return (
                        <button
                          key={`${result.url}-${index}`}
                          type="button"
                          className="demirsan-live-search-item"
                          onClick={() => handleSuggestionClick(result)}
                        >
                          <div className="demirsan-live-search-item-content">
                            {hierarchy.length > 0 && (
                              <div className="demirsan-live-search-hierarchy">
                                {hierarchy.map((item, hierarchyIndex) => (
                                  <React.Fragment
                                    key={`${item}-${hierarchyIndex}`}
                                  >
                                    {hierarchyIndex > 0 && (
                                      <span className="demirsan-live-search-hierarchy-separator">
                                        ›
                                      </span>
                                    )}

                                    <span className="demirsan-live-search-hierarchy-item">
                                      {String(item).toLocaleUpperCase("tr-TR")}
                                    </span>
                                  </React.Fragment>
                                ))}
                              </div>
                            )}
                            <div className="demirsan-live-search-title">
                              <HeaderHighlightText
                                text={result.title || "Sonuç"}
                                query={searchText}
                              />
                            </div>

                            {result.snippet && (
                              <div className="demirsan-live-search-snippet">
                                <HeaderHighlightText
                                  text={result.snippet}
                                  query={searchText}
                                />
                              </div>
                            )}
                          </div>

                          <div className="demirsan-live-search-arrow">
                            <i className="far fa-arrow-right" />
                          </div>
                        </button>
                      );
                    })}

                    <button
                      type="button"
                      className="demirsan-live-search-all"
                      onClick={handleSearchSubmit}
                    >
                      <span>“{searchText.trim()}” için tüm sonuçları gör</span>

                      <i className="far fa-long-arrow-right" />
                    </button>
                  </>
                ) : (
                  <div className="demirsan-live-search-status">
                    <i className="far fa-search" />

                    <span>Sonuç bulunamadı.</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <button
          id="close"
          type="button"
          className="search-close-icon"
          onClick={handleCloseClick}
          aria-label="Aramayı kapat"
        >
          <i className="far fa-times" />
        </button>
      </div>

      <div
        id="anywhere-home"
        className={isSearchVisible ? "bgshow" : ""}
        onClick={handleBackgroundClick}
      />

      <SideMenu isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
    </div>
  );
}

export default HeaderOne;
